import React, { useEffect, useRef } from 'react'
import {
  amPmLabelToHms,
  formatHmsAsAmPm,
  safeParseMethods,
} from '@utils/phoneManagement.utils'
import { ASSIGNED_DEVICES, CONTACT_KEYS } from '@constants/Settings'
import './EditPhoneNumberStyle.css'
import useEditPhoneNumber from './useEditPhoneNumber'
import { IWorkingHoursDataGrid } from '@dataTypes/clinic.type'
import UnsavedChangesModal from '../../UnsavedChangesModal/UnsavedChangesModal.component'
import useCommunicationsLeaveGuard from '../../shared/useCommunicationsLeaveGuard'
import {
  Breadcrumbs,
  Button,
  Checkbox,
  DataTable,
  InputField,
  MultiselectCombobox,
  Select,
  ToggleSwitch,
  type DataTableColumn,
} from '@enamelai/design-system'

type HoursRow = { id: string }

type WorkingHoursTimeSelectProps = {
  timeOpts: string[]
  value: string
  onSelect: (nextHms: string) => void
  ariaLabel: string
  hasError?: boolean
}

const WorkingHoursTimeSelect = ({
  timeOpts,
  value,
  onSelect,
  ariaLabel,
  hasError,
}: WorkingHoursTimeSelectProps): React.ReactElement => {
  const label = formatHmsAsAmPm(value)
  const options =
    timeOpts.length === 0
      ? [label]
      : timeOpts.includes(label)
        ? timeOpts
        : [label, ...timeOpts]

  return (
    <Select
      className="scp-pm-wh-time-select"
      placeholder={ariaLabel}
      label=""
      value={label}
      leadingIcon="clock"
      error={hasError}
      options={options.map((time) => ({ label: time, value: time }))}
      onChange={(next) => onSelect(amPmLabelToHms(String(next)))}
    />
  )
}

const CONTACT_OPTIONS = CONTACT_KEYS.map((key) => ({
  value: key,
  label: key === 'sms' ? 'SMS' : 'Call',
}))

const EditPhoneNumber = (): React.ReactElement | null => {
  const hooks = useEditPhoneNumber()
  const leaveGuard = useCommunicationsLeaveGuard(hooks.hasUnsavedChanges())
  const saveRef = useRef(hooks.save)
  const cancelRef = useRef(hooks.onClickCancel)
  const hoursByDay = new Map(
    hooks.workingHours.map((row) => [row.day_of_week, row]),
  )

  useEffect(() => {
    saveRef.current = hooks.save
    cancelRef.current = hooks.onClickCancel
  }, [hooks.save, hooks.onClickCancel])

  useEffect(() => {
    hooks.setHeaderButtons(
      <>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void cancelRef.current()}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={hooks.hasWorkingHoursRangeError}
          onClick={() => {
            void saveRef.current()
          }}
        >
          Save changes
        </Button>
      </>,
    )
    hooks.setHeaderTitle(
      <Breadcrumbs
        items={[
          {
            label: 'Phone management',
            onClick: () => void cancelRef.current(),
          },
          { label: 'Phone numbers' },
        ]}
        showBackIcon
        onBackClick={() => void cancelRef.current()}
      />,
    )
    return () => {
      hooks.setHeaderTitle(null)
      hooks.setHeaderButtons(null)
    }
  }, [
    hooks.setHeaderTitle,
    hooks.setHeaderButtons,
    hooks.hasWorkingHoursRangeError,
  ])

  const assignedDevice = ASSIGNED_DEVICES.find(
    (device) =>
      device.label === hooks.phoneEditor?.draft.routed_to_value ||
      `${device.type}|${device.label}` ===
        hooks.phoneEditor?.draft.routed_to_value,
  )

  const renderHours =
    (render: (row: IWorkingHoursDataGrid) => React.ReactNode) =>
    (row: HoursRow): React.ReactNode => {
      const hours = hoursByDay.get(row.id)
      return hours ? render(hours) : null
    }

  const workingHoursColumns: DataTableColumn<HoursRow>[] = [
    {
      id: 'status',
      label: 'Status',
      width: '11%',
      renderCell: renderHours((data) => (
        <ToggleSwitch
          checked={data.status}
          aria-label={`${data.day_of_week} status`}
          onChange={(checked) => {
            hooks.updateWorkingHours(
              data.day_of_week,
              'status',
              checked ? 'true' : 'false',
            )
          }}
        />
      )),
    },
    {
      id: 'days',
      label: 'Days',
      width: '46%',
      renderCell: renderHours((data) => data.day_of_week),
    },
    {
      id: 'opens_at',
      label: 'Opens at',
      width: '21%',
      renderCell: renderHours((data) => {
        const fieldError = hooks.workingHoursFieldErrors.get(data.day_of_week)
        const highlightStart =
          !!fieldError?.startInvalid &&
          (hooks.lastWorkingHoursTimeEdit === 'start_time' ||
            hooks.lastWorkingHoursTimeEdit === null)
        return (
          <WorkingHoursTimeSelect
            timeOpts={hooks.timeOpts}
            value={data.start_time}
            ariaLabel={`Opens at ${data.day_of_week}`}
            hasError={highlightStart}
            onSelect={(next) => {
              hooks.setLastWorkingHoursTimeEdit('start_time')
              hooks.updateWorkingHours(data.day_of_week, 'start_time', next)
            }}
          />
        )
      }),
    },
    {
      id: 'closes_at',
      label: 'Closes at',
      width: '21%',
      renderCell: renderHours((data) => {
        const fieldError = hooks.workingHoursFieldErrors.get(data.day_of_week)
        const highlightEnd =
          !!fieldError?.endInvalid &&
          (hooks.lastWorkingHoursTimeEdit === 'end_time' ||
            hooks.lastWorkingHoursTimeEdit === null)
        return (
          <WorkingHoursTimeSelect
            timeOpts={hooks.timeOpts}
            value={data.end_time}
            ariaLabel={`Closes at ${data.day_of_week}`}
            hasError={highlightEnd}
            onSelect={(next) => {
              hooks.setLastWorkingHoursTimeEdit('end_time')
              hooks.updateWorkingHours(data.day_of_week, 'end_time', next)
            }}
          />
        )
      }),
    },
  ]

  return (
    <>
      <div className="scp-pm-edit-phone-number">
        <div className="scp-pm-card">
          <div className="scp-pm-row-between">
            <span className="scp-pm-label">Set as main</span>
            <ToggleSwitch
              checked={!!hooks.phoneEditor?.draft.is_main}
              onChange={(checked) => hooks.setDraft({ is_main: checked })}
              aria-label="Set as main"
            />
          </div>

          <div className="scp-pm-epm-grid">
            <InputField
              variant="text"
              fullWidth
              label="Line name"
              value={hooks.phoneEditor?.draft.line_name || ''}
              onChange={(event) =>
                hooks.setDraft({ line_name: event.target.value })
              }
              clearable={false}
            />
            <InputField
              variant="text"
              fullWidth
              label="Phone number"
              value={hooks.selectedPhoneLine?.phone_number || ''}
              disabled
              clearable={false}
            />
            <MultiselectCombobox
              label="Contact methods"
              placeholder="Select"
              filterable={false}
              options={CONTACT_OPTIONS}
              value={safeParseMethods(hooks.phoneEditor?.draft.contact_methods)}
              onChange={(values) =>
                hooks.setDraft({ contact_methods: JSON.stringify(values) })
              }
            />
            <Select
              label="Assigned device"
              placeholder="Select…"
              value={
                assignedDevice
                  ? `${assignedDevice.type}|${assignedDevice.label}`
                  : undefined
              }
              options={ASSIGNED_DEVICES.map((device) => ({
                label: device.label,
                value: `${device.type}|${device.label}`,
              }))}
              onChange={(value) => {
                const [type, label] = String(value).split('|')
                hooks.setDraft({
                  routed_to_type: type,
                  routed_to_value: label,
                })
              }}
            />
            <InputField
              variant="text"
              fullWidth
              optional
              label="Extension number"
              placeholder="1000 - 9000"
              value={hooks.phoneEditor?.draft.extension_number || ''}
              onChange={(event) =>
                hooks.handleExtensionNumberChange(
                  event as React.ChangeEvent<HTMLInputElement>,
                )
              }
              error={!!hooks.extensionNumberError}
              helperText={hooks.extensionNumberError || undefined}
              clearable={false}
            />
            <div className="scp-pm-field">
              <span className="scp-pm-label">Capabilities</span>
              <div className="scp-pm-checkrow">
                <Checkbox
                  label="Send SMS"
                  checked={!!hooks.phoneEditor?.draft.functions_send_sms}
                  onChange={(event) =>
                    hooks.setDraft({
                      functions_send_sms: event.target.checked,
                    })
                  }
                />
                <Checkbox
                  label="Outbound calls"
                  checked={!!hooks.phoneEditor?.draft.functions_outbound_calls}
                  onChange={(event) =>
                    hooks.setDraft({
                      functions_outbound_calls: event.target.checked,
                    })
                  }
                />
              </div>
            </div>
          </div>
          <div className="scp-pm-grid2"></div>
        </div>

        <div className="scp-pm-card">
          <div className="scp-pm-row-between">
            <span
              className="scp-pm-label"
              title="When enabled, routing can respect these hours."
            >
              Apply working hours
            </span>
            <ToggleSwitch
              checked={!!hooks.phoneEditor?.draft.apply_working_hours}
              aria-label="Apply working hours"
              onChange={(checked) =>
                hooks.setDraft({ apply_working_hours: checked })
              }
            />
          </div>
          {hooks.phoneEditor?.draft.apply_working_hours && (
            <>
              <DataTable<HoursRow>
                className="scp-pm-wh-table"
                showActions={false}
                columns={workingHoursColumns}
                rows={hooks.workingHours.map((row) => ({
                  id: row.day_of_week,
                }))}
              />
              <div className="scp-pm-grid2">
                <Checkbox
                  label="Same working hours"
                  checked={!!hooks.phoneEditor?.draft.same_working_hours}
                  onChange={(event) => {
                    hooks.setDraft({
                      same_working_hours: event.target.checked,
                    })
                  }}
                />
                {hooks.hasWorkingHoursRangeError && (
                  <span className="ds-input-field__helper ds-input-field__helper--error">
                    {hooks.workingHoursErrorMessage}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <UnsavedChangesModal
        show={hooks.showUnsavedChangesModal}
        onConfirm={hooks.discardUnsavedChanges}
        onHide={hooks.onHide}
      />
      {leaveGuard.modal}
    </>
  )
}

export default EditPhoneNumber
